import React from 'react';
import { Alert, Box, Button, ButtonBase, CircularProgress, Skeleton, Typography } from '@mui/material';
import { CircleCheck, CircleDashed, CircleX, RotateCw, TriangleAlert } from 'lucide-react';
import { ErrorState, FilterChip, PersonCell, SectionCard, StatusPill, useDelayedFlag } from '../../../components/ui';
import type { StatusTone } from '../../../components/ui';
import type { ApprovalConfigHealth } from '../../../store/slices/adminSlice';
import { focusRing, sbg } from '../../../theme/sbgTokens';
import { formatCount, formatNumber } from '../../../utils/format';
import type { AreaHealth, CheckAreaKey } from './issueGuide';

/* ------------------------------------------------------------------------ */
/* Shared                                                                   */
/* ------------------------------------------------------------------------ */

/** Skeleton lines revealed after 300ms so fast loads never flash. */
const CardSkeleton: React.FC<{ rows: number; label: string }> = ({ rows, label }) => {
  const visible = useDelayedFlag(true);
  return (
    <Box role="status" aria-label={label} sx={{ display: 'grid', gap: 1.5, px: 2, py: 2, visibility: visible ? 'visible' : 'hidden' }}>
      {Array.from({ length: rows }).map((_, index) => (
        <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Skeleton variant="circular" width={16} height={16} />
          <Box sx={{ flex: 1, display: 'grid', gap: 0.5 }}>
            <Skeleton variant="rounded" height={14} width={`${55 + ((index * 17) % 30)}%`} />
            <Skeleton variant="rounded" height={12} width="35%" />
          </Box>
        </Box>
      ))}
    </Box>
  );
};

/**
 * A card in the Setup health region. SectionCard always titles with an h2,
 * but these cards sit inside the "Setup health" h2, so the title here is an
 * h3 (the heading outline must nest). Mirrors SectionCard's header spacing.
 */
const SideCard: React.FC<{
  id?: string;
  title: string;
  description: string;
  action?: React.ReactNode;
  flush?: boolean;
  children?: React.ReactNode;
}> = ({ id, title, description, action, flush = false, children }) => (
  <SectionCard id={id} aria-label={title} flush sx={{ scrollMarginTop: 16 }}>
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 1.5,
        px: 2.5,
        pt: 2.5,
        pb: flush ? 2.5 : 2,
        ...(flush ? { borderBottom: `1px solid ${sbg.border.subtle}` } : null),
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography
          id={id ? `${id}-title` : undefined}
          tabIndex={id ? -1 : undefined}
          variant="h4"
          component="h3"
          sx={{ color: sbg.text.primary }}
        >
          {title}
        </Typography>
        <Typography variant="body2" component="p" sx={{ color: sbg.text.secondary, mt: 0.25 }}>
          {description}
        </Typography>
      </Box>
      {action && <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 1, mt: -0.5 }}>{action}</Box>}
    </Box>
    <Box sx={flush ? undefined : { px: 2.5, pb: 2.5 }}>{children}</Box>
  </SectionCard>
);

/** A refetch button that says it is working (spinner, present participle, aria-busy). */
export const CheckAgainButton: React.FC<{
  onClick: () => void;
  busy: boolean;
  label?: string;
  color?: 'inherit' | 'primary';
  variant?: 'outlined' | 'text';
}> = ({ onClick, busy, label = 'Check again', color = 'primary', variant = 'outlined' }) => (
  <Button
    variant={variant}
    color={color}
    size="small"
    onClick={onClick}
    disabled={busy}
    aria-busy={busy || undefined}
    startIcon={busy ? <CircularProgress size={16} thickness={5} color="inherit" aria-hidden="true" /> : <RotateCw />}
  >
    {busy ? 'Checking…' : label}
  </Button>
);

/** A list of plain sentences (problems, warnings) with real list markup. */
const SentenceList: React.FC<{ items: string[] }> = ({ items }) => (
  <Box component="ul" sx={{ m: 0, pl: 2.5, display: 'grid', gap: 0.5 }}>
    {items.map((item, index) => (
      <Typography key={`${index}-${item}`} component="li" variant="body2" sx={{ color: 'inherit', overflowWrap: 'anywhere' }}>
        {item}
      </Typography>
    ))}
  </Box>
);

/* ------------------------------------------------------------------------ */
/* Checks: the health checklist by area                                     */
/* ------------------------------------------------------------------------ */

export type Coverage = { state: 'full' | 'partial' | 'none'; reason?: string };

type RowState = 'ok' | 'broken' | 'partial' | 'partly' | 'skipped';

const ROW_ICON: Record<RowState, { Icon: typeof CircleCheck; tone: StatusTone }> = {
  ok: { Icon: CircleCheck, tone: 'success' },
  broken: { Icon: CircleX, tone: 'danger' },
  partial: { Icon: TriangleAlert, tone: 'warning' },
  partly: { Icon: CircleDashed, tone: 'neutral' },
  skipped: { Icon: CircleDashed, tone: 'neutral' },
};

const rowState = (broken: number, partial: number, coverage: Coverage): RowState =>
  coverage.state === 'none'
    ? 'skipped'
    : broken > 0
      ? 'broken'
      : partial > 0
        ? 'partial'
        : coverage.state === 'partial'
          ? 'partly'
          : 'ok';

const statusLine = (state: RowState, broken: number, partial: number, coverage: Coverage) => {
  const counts = [
    broken > 0 ? `${formatNumber(broken)} broken` : null,
    partial > 0 ? `${formatNumber(partial)} partial` : null,
  ].filter(Boolean).join(' · ');
  switch (state) {
    case 'skipped':
      return `Not checked: ${coverage.reason ?? 'its data didn’t load'}`;
    case 'partly':
      return `No issues found, partly checked: ${coverage.reason ?? 'some data didn’t load'}`;
    case 'ok':
      return 'All set';
    default:
      return coverage.state === 'partial' ? `${counts} · partly checked` : counts;
  }
};

export const ChecksCard: React.FC<{
  health: AreaHealth[];
  coverage: Record<CheckAreaKey, Coverage>;
  selected: CheckAreaKey | null;
  onSelect: (area: CheckAreaKey | null) => void;
  loading: boolean;
}> = ({ health, coverage, selected, onSelect, loading }) => (
  <SideCard
    title="Checks"
    description="What the audit looks at on every active record. Select a check with issues to filter the list."
    flush
  >
    {loading ? (
      <CardSkeleton rows={6} label="Loading checks" />
    ) : (
      <Box component="ul" aria-label="Checks by area" sx={{ listStyle: 'none', m: 0, p: 1, display: 'grid', gap: 0.5 }}>
        {health.map(({ area, broken, partial }) => {
          const areaCoverage = coverage[area.key];
          const total = broken + partial;
          const state = rowState(broken, partial, areaCoverage);
          const { Icon, tone } = ROW_ICON[state];
          const isSelected = selected === area.key;
          const content = (
            <>
              <Box component="span" aria-hidden="true" sx={{ display: 'inline-flex', color: sbg.status[tone].icon, pt: '2px', flex: 'none' }}>
                <Icon size={16} />
              </Box>
              <Box component="span" sx={{ flex: 1, minWidth: 0, display: 'grid', gap: 0.25, textAlign: 'left' }}>
                <Typography variant="labelMd" component="span" sx={{ color: sbg.text.primary }}>
                  {area.label}
                </Typography>
                <Typography variant="body2" component="span" sx={{ color: sbg.text.secondary }}>
                  {statusLine(state, broken, partial, areaCoverage)}
                </Typography>
              </Box>
              {total > 0 && (
                <Typography
                  variant="body2"
                  component="span"
                  sx={{ color: sbg.text.secondary, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', pt: '1px' }}
                >
                  {formatCount(total, 'employee')}
                </Typography>
              )}
            </>
          );
          const rowSx = {
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1,
            width: '100%',
            px: 1.5,
            py: 1,
            borderRadius: sbg.radius.md,
          } as const;
          return (
            <li key={area.key}>
              {total > 0 ? (
                <ButtonBase
                  aria-pressed={isSelected}
                  onClick={() => onSelect(isSelected ? null : area.key)}
                  sx={{
                    ...rowSx,
                    justifyContent: 'flex-start',
                    bgcolor: isSelected ? sbg.bg.selected : 'transparent',
                    transition: `background-color ${sbg.duration.fast} ${sbg.ease.standard}`,
                    '&:hover': { bgcolor: isSelected ? sbg.bg.selectedStrong : sbg.bg.hover },
                    '&.Mui-focusVisible': focusRing,
                  }}
                >
                  {content}
                </ButtonBase>
              ) : (
                <Box sx={rowSx}>{content}</Box>
              )}
            </li>
          );
        })}
      </Box>
    )}
  </SideCard>
);

/**
 * The Checks card's filter, compact: one chip per area with issues. Used
 * where the side column stacks, so the filter sits above the list it filters
 * instead of after it.
 */
export const CheckAreaChips: React.FC<{
  health: AreaHealth[];
  selected: CheckAreaKey | null;
  onSelect: (area: CheckAreaKey | null) => void;
}> = ({ health, selected, onSelect }) => {
  const withIssues = health.filter(({ broken, partial }) => broken + partial > 0);
  if (withIssues.length === 0) return null;
  return (
    <Box role="group" aria-label="Filter by check" sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
      {withIssues.map(({ area, broken, partial }) => {
        const isSelected = selected === area.key;
        return (
          <FilterChip
            key={area.key}
            selected={isSelected}
            onClick={() => onSelect(isSelected ? null : area.key)}
            count={broken + partial}
          >
            {area.label}
          </FilterChip>
        );
      })}
    </Box>
  );
};

/* ------------------------------------------------------------------------ */
/* Sales WFH approval routing                                               */
/* ------------------------------------------------------------------------ */

// "A", "A and B", or "A, B, and C", matching the backend's reviewer copy.
export const formatReviewerList = (items: string[]) => {
  if (items.length <= 1) return items.join('');
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
};

const FieldLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="labelSm" component="h4" sx={{ color: sbg.text.secondary, m: 0 }}>
    {children}
  </Typography>
);

/** Element id of the Sales WFH approvals card, for "View details" jumps. */
export const APPROVAL_CARD_ID = 'setup-health-sales-wfh';

export const ApprovalRoutingCard: React.FC<{
  config: ApprovalConfigHealth | undefined;
  loading: boolean;
  /** A refetch is in flight (after the first load). */
  fetching: boolean;
  error: boolean;
  /** Missing or broken server settings: approvals can't fully work. */
  problems: string[];
  /** Degraded but working: skipped reviewer addresses, escalation gaps. */
  warnings: string[];
  onRetry: () => void;
}> = ({ config, loading, fetching, error, problems, warnings, onRetry }) => {
  // Turning WFH emails off is a deliberate server setting, not missing
  // config, so on its own it reads as "Turned off" rather than a failure.
  const emailsOff = Boolean(config?.emailsDisabled);
  const status: { tone: StatusTone; label: string } | null = loading
    ? null
    : error
      ? { tone: 'neutral', label: 'Not checked' }
      : !config
        ? null
        : problems.length > 0
          ? { tone: 'danger', label: 'Not configured' }
          : warnings.length > 0
            ? { tone: 'warning', label: 'Needs attention' }
            : emailsOff
              ? { tone: 'neutral', label: 'Turned off' }
              : { tone: 'success', label: 'Configured' };

  const emailsOffNote = emailsOff && (
    <Alert severity="info">
      WFH emails are turned off on the server, so reviewers get no request, reminder or escalation emails.
    </Alert>
  );
  const warningsNote = warnings.length > 0 && (
    <Alert severity="warning">
      <SentenceList items={warnings} />
    </Alert>
  );

  return (
    <SideCard
      id={APPROVAL_CARD_ID}
      title="Sales WFH approvals"
      description="Who reviews sales WFH requests and who is copied when a review runs late."
      action={status && <StatusPill tone={status.tone}>{status.label}</StatusPill>}
      flush={loading || error}
    >
      {loading ? (
        <CardSkeleton rows={3} label="Loading sales approval settings" />
      ) : error ? (
        <ErrorState
          compact
          title="Couldn’t check sales approvals"
          body="The approval settings didn’t load, so reviewer routing and overdue escalations weren’t checked."
          onRetry={onRetry}
          retryLabel="Check again"
        />
      ) : !config ? null : problems.length > 0 ? (
        <Box sx={{ display: 'grid', gap: 1.5 }}>
          <Alert severity="error">
            <SentenceList items={problems} />
          </Alert>
          {warningsNote}
          {emailsOffNote}
          <Typography variant="body2" sx={{ color: sbg.text.secondary }}>
            These are server settings, so an engineer needs to change them. Check again once they have.
          </Typography>
          <Box>
            <CheckAgainButton onClick={onRetry} busy={fetching} />
          </Box>
        </Box>
      ) : (
        <Box sx={{ display: 'grid', gap: 2 }}>
          <Box sx={{ display: 'grid', gap: 1 }}>
            <FieldLabel>Reviewers</FieldLabel>
            {config.resolvedSalesVps.length > 0 ? (
              <Box component="ul" aria-label="Sales WFH reviewers" sx={{ listStyle: 'none', m: 0, p: 0, display: 'grid', gap: 1 }}>
                {config.resolvedSalesVps.map((vp) => (
                  <li key={vp.id}>
                    <PersonCell size={24} name={vp.name} secondary={vp.email} />
                  </li>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" sx={{ color: sbg.text.secondary }}>
                No reviewers resolve to an active employee.
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'grid', gap: 0.5 }}>
            <FieldLabel>Overdue escalations</FieldLabel>
            <Typography variant="body2" sx={{ color: sbg.text.primary, overflowWrap: 'anywhere' }}>
              {(config.externalEscalationEmails || []).length > 0
                ? `Also copied to ${formatReviewerList(config.externalEscalationEmails)}.`
                : 'No one outside the reviewer pool is copied.'}
            </Typography>
          </Box>
          {warningsNote}
          {emailsOffNote}
        </Box>
      )}
    </SideCard>
  );
};
